'use client';
import * as React from 'react';
import {
  ArticleCard,
  type ArticleCardProps,
} from '@/hen-ui/composites/ArticleCard';
import { ImageCard } from '@/hen-ui/composites/ImageCard';
import { Button, type ButtonProps } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Link } from '@/hen-ui/atomics/Link';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import {
  Carousel,
  CarouselItem,
  type CarouselApi,
} from '@/hen-ui/composites/Carousel';
import { Flex } from '@/hen-ui/composites/Flex';
import { Grid, type GridProps } from '@/hen-ui/composites/Grid';
import { Section } from '@/hen-ui/composites/Section';
import { cn } from '@/hen-ui/utils';
import { articleRowContainerVariants } from './article-row.variants';
import { VerticalArticleRow } from './VerticalArticleRow';

type BaseArticleRowProps = {
  /** Optional className for container */
  className?: string;
};

type HeroArticleRowProps = BaseArticleRowProps & {
  variant: 'hero';
  /** Full-width background image URL (required for hero variant) */
  heroImage: string;
  /** Eyebrow text */
  heroBrow?: string;
  /** Overlay title */
  heroTitle?: string;
  /** Overlay subtitle */
  heroSubtext?: string;
  /** CTA button configuration */
  heroAction?: {
    label: string;
  } & Omit<ButtonProps, 'children' | 'variant' | 'size'>;
  /** Overlay text alignment (full-width hero) */
  heroAlign?: 'left' | 'center';
  /**
   * Optional content rendered inside the hero overlay, below the
   * brow/title/subtext (and CTA). Use to embed interactive content such as a
   * Dynamic Experience / DCS within the hero.
   */
  children?: React.ReactNode;
};

/**
 * Allowed grid gap tokens for the `2/3/4-col` variants. Derived from the `Grid`
 * composite so the set can never drift from the real grid spacing tokens.
 */
export type GridGap = NonNullable<GridProps['gap']>;

/**
 * Runtime token list reused by the ArticleRow shortcode (attr validation) and
 * stories. `satisfies` ties it to {@link GridGap}, so removing or renaming a
 * `Grid` gap token fails the build here instead of drifting silently.
 */
export const GRID_GAPS = [
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  'max',
] as const satisfies readonly GridGap[];

type ContentArticleRowProps = BaseArticleRowProps & {
  variant: '2-col' | '3-col' | '4-col' | 'carousel' | 'vertical';
  /** Array of article data matching ArticleCard props */
  articles?: ArticleCardProps[];
  /**
   * Gap between cards in the `2-col`/`3-col`/`4-col` grid (HEN spacing token).
   * Defaults to `xl` to preserve existing grids; pass `md` to match the Figma
   * card-grid spec (20px at desktop). Ignored by `carousel`/`vertical`.
   */
  gap?: GridGap;
  /** Children (ArticleCard components) - alternative to articles prop */
  children?: React.ReactNode;
  /**
   * Default hover elevation for cards built from `articles` (`shadow-md` at `md+`).
   * Per-card {@link ArticleCardProps.hoverStyle} overrides when provided.
   * Omit for no row-level default (cards use `ArticleCard`'s default `none`).
   */
  cardHoverStyle?: ArticleCardProps['hoverStyle'];
  /**
   * URL for a CTA link rendered below the row content.
   * - `2-col` / `3-col` / `4-col`: rendered right-aligned below the grid.
   * - `carousel`: rendered right-aligned below the carousel arrows row.
   * - `vertical`: rendered in place of the pagination arrows (arrows suppressed,
   *   only the first page of articles is shown).
   */
  link?: string;
  /** Label text for the CTA link. */
  linkLabel?: string;
  /**
   * Variant for the CTA `Link`. Opt-in override; omit to keep each variant's
   * built-in treatment: `carousel` defaults to `primary`, while `2/3/4-col` and
   * `vertical` default to the base link color. Pass e.g. `primary` to brand the
   * grid/vertical CTA with the tenant's `primary-700`.
   */
  linkVariant?: React.ComponentProps<typeof Link>['variant'];
};

export type ArticleRowProps = HeroArticleRowProps | ContentArticleRowProps;

/** Stable list keys for carousel slides (reuse element keys from ArticleCard / children). */
function getCarouselSlideKey(node: React.ReactNode, index: number): React.Key {
  if (React.isValidElement(node) && node.key != null) {
    return node.key;
  }
  return `slide-${index}`;
}

/**
 * ArticleRow component following HEN Design System standards.
 *
 * A section-level composite component that displays Article Card components
 * in various grid layouts or a full-width Image Card hero with overlay content.
 * Standardizes article collection displays across HEN applications.
 *
 * @example
 * // Hero variant with ImageCard Full Width
 * <ArticleRow
 *   variant="hero"
 *   heroImage="/images/hero-bg.jpg"
 *   heroBrow="Technology"
 *   heroTitle="Discover Your Future"
 *   heroSubtext="Experience an immersive learning journey"
 *   heroAction={{ label: "Learn More", href: "/about" }}
 * />
 *
 * @example
 * // 3-column grid with articles array
 * <ArticleRow
 *   variant="3-col"
 *   articles={[
 *     { image: "/img1.jpg", imageAlt: "Article 1", title: "Title 1", href: "/article-1" },
 *     { image: "/img2.jpg", imageAlt: "Article 2", title: "Title 2", href: "/article-2" },
 *     { image: "/img3.jpg", imageAlt: "Article 3", title: "Title 3", href: "/article-3" },
 *   ]}
 * />
 *
 * @example
 * // 3-column grid with children components
 * <ArticleRow variant="3-col">
 *   <ArticleCard image="/img1.jpg" imageAlt="Article 1" title="Title 1" href="/article-1" />
 *   <ArticleCard image="/img2.jpg" imageAlt="Article 2" title="Title 2" href="/article-2" />
 *   <ArticleCard image="/img3.jpg" imageAlt="Article 3" title="Title 3" href="/article-3" />
 * </ArticleRow>
 *
 * @example
 * // Carousel with side arrows
 * <ArticleRow
 *   variant="carousel"
 *   articles={articles}
 * />
 */
/**
 * Variant dispatcher — renders the appropriate sub-layout based on `variant`:
 *   hero       → full-width ImageCard with overlay
 *   carousel   → embla-powered horizontal carousel with side arrows + optional CTA link
 *   vertical   → delegates to VerticalArticleRow (paginated slide list)
 *   2/3/4-col  → responsive CSS grid with optional CTA link
 */
export const ArticleRow = React.forwardRef<HTMLDivElement, ArticleRowProps>(
  (props: ArticleRowProps, ref) => {
    const { variant, className } = props;

    // Carousel state — track embla API to enable/disable arrow buttons.
    // These are only used when variant === 'carousel' but are lightweight
    // enough that unconditional initialization is acceptable.
    const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    React.useEffect(() => {
      if (!carouselApi) return;

      const onUpdate = () => {
        setCanScrollPrev(carouselApi.canScrollPrev());
        setCanScrollNext(carouselApi.canScrollNext());
      };

      onUpdate();
      carouselApi.on('select', onUpdate);
      carouselApi.on('reInit', onUpdate);
      return () => {
        carouselApi.off('select', onUpdate);
        carouselApi.off('reInit', onUpdate);
      };
    }, [carouselApi]);

    // Hero variant renders ImageCard Full Width with overlay content
    if (variant === 'hero') {
      const {
        heroImage,
        heroBrow,
        heroTitle,
        heroSubtext,
        heroAction,
        heroAlign = 'center',
        children,
      } = props;
      const headerAlign = heroAlign === 'left' ? 'left' : 'center';
      const flexAlign = heroAlign === 'left' ? 'start' : 'center';
      let heroActionNode: React.ReactNode = null;
      if (heroAction) {
        const { label: heroActionLabel, ...heroActionProps } = heroAction;
        if (heroActionProps.href) {
          const { href, ...anchorActionProps } = heroActionProps;
          heroActionNode = (
            <Button
              {...anchorActionProps}
              variant='primaryFill'
              size='sm'
              href={href}
            >
              {heroActionLabel}
            </Button>
          );
        } else if (heroActionProps.onClick) {
          const {
            href: _href,
            target: _target,
            rel: _rel,
            ...buttonActionProps
          } = heroActionProps;
          heroActionNode = (
            <Button
              {...buttonActionProps}
              type='button'
              variant='primaryFill'
              size='sm'
            >
              {heroActionLabel}
            </Button>
          );
        }
      }
      return (
        <Flex
          ref={ref}
          className={cn(articleRowContainerVariants({ variant }), className)}
        >
          <ImageCard
            variant='full-width'
            imageUrl={heroImage}
            imageAlt={heroTitle || 'Hero image'}
            align={heroAlign}
          >
            <Flex
              direction='column'
              align={flexAlign}
              gap='lg'
              className='w-full'
            >
              <SectionHeader
                size='h1'
                align={headerAlign}
                color='white'
                brow={heroBrow}
                title={heroTitle || ''}
                subtext={heroSubtext}
                className='w-full lg:w-1/2'
              />
              {heroActionNode}
              {children && <div className='w-full'>{children}</div>}
            </Flex>
          </ImageCard>
        </Flex>
      );
    }

    const {
      articles = [],
      children,
      link,
      linkLabel,
      linkVariant,
      cardHoverStyle,
      gap = 'xl',
    } = props;

    // Carousel variant: native horizontal scroll on mobile (no Embla, no arrows);
    // `md+` keeps Embla + side arrows. Does not affect `vertical` (pagination arrows only).
    if (variant === 'carousel') {
      const slideElements = children
        ? React.Children.toArray(children)
        : articles.map((article, index) => (
            <ArticleCard
              key={article.href ?? `article-${index}`}
              {...article}
              hoverStyle={article.hoverStyle ?? cardHoverStyle}
            />
          ));

      const emblaSlides = slideElements.map((node, index) => (
        <CarouselItem
          key={getCarouselSlideKey(node, index)}
          basis='full'
          className='md:basis-1/2 lg:basis-1/3'
        >
          {node}
        </CarouselItem>
      ));

      const carouselArrowButtonClass =
        'inline-flex p-0 w-8 h-8 rounded-sm min-h-8 min-w-8 shrink-0';

      return (
        <Flex
          ref={ref}
          className={cn(articleRowContainerVariants({ variant }), className)}
        >
          {/* Mobile: finger scrolling only — avoids Embla + full-card links fighting touch */}
          <Section
            as='div'
            padding='none'
            gap='md'
            role='region'
            aria-label='Articles'
            tabIndex={0}
            onKeyDown={handleMobileArticleCarouselKeyDown}
            className={cn(
              'flex-row w-full min-w-0 snap-x snap-mandatory overflow-x-auto overscroll-x-contain touch-pan-x',
              '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
              'rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
              'md:hidden',
            )}
          >
            {slideElements.map((node, index) => (
              // Fixed 280px slide so the next card peeks (Figma "Article Scroll"
              // mobile spec) instead of one card filling the viewport.
              <Section
                as='div'
                key={getCarouselSlideKey(node, index)}
                padding='none'
                gap='min'
                className='min-w-0 shrink-0 grow-0 basis-auto w-article-card-mobile snap-start snap-always gap-0'
              >
                {node}
              </Section>
            ))}
          </Section>

          <Flex
            direction='row'
            align='center'
            gap='md'
            className='hidden md:flex w-full min-w-0'
          >
            {(canScrollPrev || canScrollNext) && (
              <Button
                variant='grayOutline'
                size='sm'
                className={carouselArrowButtonClass}
                onClick={() => carouselApi?.scrollPrev()}
                disabled={!canScrollPrev}
                aria-label='Previous articles'
              >
                <Icon icon='arrow-left' />
              </Button>
            )}

            {/* `-my-2 py-2` (paired with the Carousel viewport below) keeps card
                borders / hover shadows from being clipped by the two overflow-hidden
                layers without shifting layout. */}
            <Flex className='overflow-hidden flex-1 min-w-0 -my-2 py-2'>
              <Carousel
                setApi={setCarouselApi}
                autoPlay={false}
                opts={{
                  align: 'start',
                  loop: true,
                  slidesToScroll: 1,
                }}
                showControls={false}
                showDots={false}
                viewportClassName='-my-2 py-2'
              >
                {emblaSlides}
              </Carousel>
            </Flex>

            {(canScrollPrev || canScrollNext) && (
              <Button
                variant='grayOutline'
                size='sm'
                className={carouselArrowButtonClass}
                onClick={() => carouselApi?.scrollNext()}
                disabled={!canScrollNext}
                aria-label='Next articles'
              >
                <Icon icon='arrow-right' />
              </Button>
            )}
          </Flex>

          {link && (
            // Carousel "View All" CTA defaults to a primary, b5-sized text link
            // (BC Home "Latest News" treatment). `linkVariant` overrides the
            // color when provided. text-b5 is the HEN body-5 typography token;
            // Link's size prop only exposes raw text-sm/base/lg, so the token is
            // applied via className.
            <Flex direction='row' gap='xs' justify='end'>
              <Link
                href={link}
                weight='bold'
                variant={linkVariant ?? 'primary'}
                className='text-b5'
              >
                {linkLabel}
              </Link>
            </Flex>
          )}
        </Flex>
      );
    }

    // Vertical variant delegates to its own component (owns slide state internally)
    if (variant === 'vertical') {
      return (
        <VerticalArticleRow
          ref={ref}
          articles={articles}
          link={link}
          linkLabel={linkLabel}
          linkVariant={linkVariant}
          className={className}
        >
          {children}
        </VerticalArticleRow>
      );
    }

    // Grid variants (2-col, 3-col, 4-col).
    // Optionally renders a bold CTA link below the grid when `link` is provided.
    // CTA color defaults to the base link color; `linkVariant` opts into e.g. primary.
    return (
      <Flex
        ref={ref}
        direction='column'
        gap='md'
        className={cn(articleRowContainerVariants({ variant }), className)}
      >
        <Grid
          columns={1}
          columns-md={2}
          columns-lg={
            variant === '3-col' ? 3 : variant === '4-col' ? 4 : undefined
          }
          gap={gap}
          role='list'
          aria-label='Article grid'
        >
          {children
            ? React.Children.map(children, (child, index) => (
                <Flex key={index} role='listitem'>
                  {child}
                </Flex>
              ))
            : articles.map((article, index) => (
                <Flex key={index} role='listitem'>
                  <ArticleCard
                    {...article}
                    hoverStyle={article.hoverStyle ?? cardHoverStyle}
                  />
                </Flex>
              ))}
        </Grid>
        {link && (
          // CTA uses HEN body-5 typography (text-b5) to match the carousel
          // treatment; Link's size prop only exposes text-sm/base/lg, so the
          // token is applied via className.
          <Flex direction='row' gap='xs' justify='end'>
            <Link
              href={link}
              variant={linkVariant}
              weight='bold'
              className='text-b5'
            >
              {linkLabel}
            </Link>
          </Flex>
        )}
      </Flex>
    );
  },
);

ArticleRow.displayName = 'ArticleRow';

/** Keyboard scroll for the mobile-only horizontal strip (`variant="carousel"`). */
function handleMobileArticleCarouselKeyDown(
  event: React.KeyboardEvent<HTMLDivElement>,
) {
  const el = event.currentTarget;
  const page = el.clientWidth;
  if (page <= 0) {
    return;
  }

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      el.scrollBy({ left: -page, behavior: 'smooth' });
      break;
    case 'ArrowRight':
      event.preventDefault();
      el.scrollBy({ left: page, behavior: 'smooth' });
      break;
    case 'Home':
      event.preventDefault();
      el.scrollTo({ left: 0, behavior: 'smooth' });
      break;
    case 'End':
      event.preventDefault();
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      break;
    default:
      break;
  }
}
